import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingBarOrEmptySpace } from 'components/common/loading/loading-bar-or-empty-space';
import { FC, memo, useMemo, useState } from 'react';
import { statesAndCities } from '../cities';

export interface PhoneSearchCriteria {
  areaCode?: string;
  city?: string;
  region?: string;
  startsWith?: string;
  limit: number;
}

export interface PhoneOption {
  phoneNumber: string;
  location: string | null;
  oneTimeCost?: string;
  monthlyCost?: string;
}

type Mode = 'areaCode' | 'cityState' | 'startsWith';

/** Inventory moves constantly, so an empty result is routine rather than an error. */
const MODES: { value: Mode; label: string; emptyHint: string }[] = [
  {
    value: 'areaCode',
    label: 'Area code',
    emptyHint: 'No numbers available in that area code right now. Try a nearby one.',
  },
  {
    value: 'cityState',
    label: 'City and state',
    emptyHint: 'No numbers available in that city right now. Try a nearby city.',
  },
  {
    value: 'startsWith',
    label: 'Number starts with',
    emptyHint: 'No numbers start with those digits right now. Try fewer digits.',
  },
];

const emptyHintFor = (mode: Mode) => MODES.find((m) => m.value === mode)?.emptyHint;

/** A page a client can actually read through; they only need one number. */
const CLIENT_RESULT_COUNTS = [10, 25, 50, 100];

/** Admins browse inventory. 500 is Telnyx's ceiling; beyond it the vendor silently truncates. */
export const ADMIN_RESULT_COUNTS = [50, 100, 250, 500];

const STATES = Object.keys(statesAndCities).sort();

/** Both numeric fields go straight into a Telnyx filter, so anything else is a wasted round trip. */
const digitsOnly = (v: string) => v.replace(/\D/g, '');

/** Memoised so picking a number does not re-render every sibling row. */
const PhoneRow = memo<{ option: PhoneOption; showCost: boolean }>(({ option, showCost }) => (
  <FormControlLabel
    value={option.phoneNumber}
    control={<Radio disableRipple />}
    label={
      <Stack direction="row" spacing={2} alignItems="baseline">
        <Typography>{option.phoneNumber}</Typography>
        {option.location && (
          <Typography variant="caption" color="text.secondary">
            {option.location}
          </Typography>
        )}
        {showCost && (
          <Typography variant="caption" color="text.secondary">
            ${Number(option.oneTimeCost)} + ${Number(option.monthlyCost)}/month
          </Typography>
        )}
      </Stack>
    }
  />
));
PhoneRow.displayName = 'PhoneRow';

interface Props {
  value: string;
  onChange: (phoneNumber: string) => void;
  /** Must not cache: inventory is live, and a cached list offers numbers already sold. */
  onSearch: (criteria: PhoneSearchCriteria) => Promise<PhoneOption[]>;
  /** Internal surfaces show what Aktify pays the vendor; client surfaces must not. */
  showCost?: boolean;
  /** Admin surfaces pass ADMIN_RESULT_COUNTS to browse deeper than a client needs to. */
  resultCounts?: number[];
}

/** Search Telnyx inventory and pick one number. */
export const PhoneNumberPicker: FC<Props> = ({
  value,
  onChange,
  onSearch,
  showCost = false,
  resultCounts = CLIENT_RESULT_COUNTS,
}) => {
  const [mode, setMode] = useState<Mode>('areaCode');
  const [areaCode, setAreaCode] = useState('');
  const [startsWith, setStartsWith] = useState('');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');
  const [limit, setLimit] = useState(resultCounts[0]);
  // Carries its own mode, so switching the dropdown cannot rewrite the hint for a search
  // that already ran.
  const [results, setResults] = useState<{ mode: Mode; options: PhoneOption[] } | null>(null);
  const [searching, setSearching] = useState(false);

  const cities = useMemo(() => [...(statesAndCities[region] ?? [])].sort(), [region]);

  // Only the active mode's fields travel, so switching mode cannot smuggle a stale value.
  const criteria = (): PhoneSearchCriteria | null => {
    if (mode === 'areaCode') return areaCode ? { areaCode, limit } : null;
    if (mode === 'startsWith') return startsWith ? { startsWith, limit } : null;
    return city && region ? { city, region, limit } : null;
  };

  const searchCriteria = criteria();

  const runSearch = async () => {
    if (!searchCriteria) return;
    // The picked number belongs to the list being replaced; leaving it selected let Finish
    // provision a number no longer on screen.
    onChange('');
    setSearching(true);
    try {
      setResults({ mode, options: await onSearch(searchCriteria) });
    } finally {
      setSearching(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ width: 190 }}>
          <InputLabel id="phone-search-mode">Search by</InputLabel>
          <Select
            labelId="phone-search-mode"
            label="Search by"
            value={mode}
            onChange={(e) => setMode(e.target.value as Mode)}
          >
            {MODES.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {mode === 'areaCode' && (
          <TextField
            size="small"
            label="Area code"
            value={areaCode}
            onChange={(e) => setAreaCode(digitsOnly(e.target.value))}
            inputProps={{ inputMode: 'numeric' }}
            sx={{ width: 140 }}
          />
        )}

        {mode === 'startsWith' && (
          <TextField
            size="small"
            label="Number starts with"
            value={startsWith}
            onChange={(e) => setStartsWith(digitsOnly(e.target.value))}
            inputProps={{ inputMode: 'numeric' }}
            sx={{ width: 200 }}
          />
        )}

        {mode === 'cityState' && (
          <>
            <FormControl size="small" sx={{ width: 110 }}>
              <InputLabel id="phone-search-state">State</InputLabel>
              <Select
                labelId="phone-search-state"
                label="State"
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                  setCity('');
                }}
              >
                {STATES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* freeSolo: our city list is not exhaustive, and Telnyx accepts any city name. */}
            <Autocomplete
              freeSolo
              size="small"
              options={cities}
              // The typed text is the value: with freeSolo a `value` prop is resolved against
              // `options` on blur, which discards any city the list does not know.
              inputValue={city}
              onInputChange={(_, next) => setCity(next)}
              sx={{ width: 280 }}
              renderInput={(params) => <TextField {...params} label="City" />}
            />
          </>
        )}

        <FormControl size="small" sx={{ width: 110 }}>
          <InputLabel id="phone-search-limit">Show</InputLabel>
          <Select
            labelId="phone-search-limit"
            label="Show"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            {resultCounts.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={runSearch}
          // Nothing to search on is a guaranteed-empty round trip.
          disabled={searching || !searchCriteria}
          // Fixed width so the longer label cannot resize it. The theme keeps `contained`
          // black while disabled, but not its label.
          sx={{ width: 120, '&.Mui-disabled': { color: 'common.white', opacity: 0.7 } }}
        >
          {searching ? 'Searching' : 'Search'}
        </Button>
      </Stack>

      <LoadingBarOrEmptySpace loading={searching} />

      {results?.options.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {emptyHintFor(results.mode)}
        </Typography>
      )}

      {!!results?.options.length && (
        <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 1 }}>
          <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
            {results.options.map((option) => (
              <PhoneRow key={option.phoneNumber} option={option} showCost={showCost} />
            ))}
          </RadioGroup>
        </Box>
      )}
    </Stack>
  );
};
